import type { AvimarkDocumentSummary } from "@common/lib/api/avimarkEditor";
import { MONITORING_DOCUMENT_TYPE_ID } from "@common/utils/monitoring";

/** EN-1249 D3: the four statuses. No "Can't export" in v1. */
export type HubDocumentStatus = "ready" | "needs-setup" | "export-off" | "built-in";

export interface HubDocumentRow {
    status: HubDocumentStatus;
    detail: string;
    actionLabel: "Set up" | "Edit matches" | null;
}

// While an account-wide refusal applies (export off, exam setup not done) the server never runs the
// document checks, so judge the document itself here. The exam setup block shows the account-wide reason.
const ACCOUNT_LEVEL = new Set(["avimark_export_disabled", "avimark_setup_incomplete"]);

export function hubDocumentRow(documentTypeId: string, summary: AvimarkDocumentSummary | undefined): HubDocumentRow {
    if (documentTypeId === MONITORING_DOCUMENT_TYPE_ID) {
        return {
            status: "built-in",
            detail: "Each check-in becomes its own exam when you press Export",
            actionLabel: null,
        };
    }
    if (summary && !summary.isActive)
        return { status: "export-off", detail: "Not sent to AVImark", actionLabel: "Edit matches" };
    const readiness = summary?.readiness;
    const documentLooksReady = (summary?.matchedCount ?? 0) > 0;
    const readyByServer =
        readiness?.ok === true || (readiness?.ok === false && ACCOUNT_LEVEL.has(readiness.code) && documentLooksReady);
    if (summary?.confirmedAt && readyByServer) {
        const matched = summary.matchedCount ?? 0;
        return {
            status: "ready",
            detail: `${matched} ${matched === 1 ? "field" : "fields"} matched`,
            actionLabel: "Edit matches",
        };
    }
    return { status: "needs-setup", detail: "Exports are paused until this is set up", actionLabel: "Set up" };
}

/** D5: one line above the table while nothing (Monitoring aside) is Ready. */
export function showNewClinicLine(rows: { documentTypeId: string; row: HubDocumentRow }[]): boolean {
    return !rows.some((r) => r.documentTypeId !== MONITORING_DOCUMENT_TYPE_ID && r.row.status === "ready");
}
